import React, { useMemo } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { calculateRouteCost } from '../../logistics/components/LogisticsUtils';
import { buildDriverDisplayName, buildTransportDisplayName, buildTransportMeta } from '../../logistics/components/driverUtils';
import {
  getOfficeLocationFromSettings,
  getTimestampMillis,
  calculateDistance,
  formatCurrency
} from '../utils/routeMonitorUtils';

const getRouteDriverLabel = (route) =>
  buildDriverDisplayName(
    {
      driverName: route?.driverName,
      driverLastName: route?.driverLastName,
      currentDriverName: route?.currentDriverName,
      currentDriverLastName: route?.currentDriverLastName,
      driverDisplayName: route?.driverDisplayName,
      choferName: route?.choferName
    },
    'Chofer no registrado'
  );

const getRouteTransportLabel = (route) =>
  buildTransportDisplayName(
    {
      transportName: route?.transportName,
      transporterName: route?.transporterName,
      transportLabel: route?.transportLabel,
      nameOfTransport: route?.nameOfTransport,
      transportModel: route?.transportModel,
      vehicleLabel: route?.vehicleLabel,
      patent: route?.patent,
      vehiclePatent: route?.vehiclePatent,
      transportType: route?.transportType,
      transport: route?.transport
    },
    'Unidad no registrada'
  );

const getRouteTransportMeta = (route) =>
  buildTransportMeta({
    transportName: route?.transportName,
    transporterName: route?.transporterName,
    transportModel: route?.transportModel,
    vehicleLabel: route?.vehicleLabel,
    patent: route?.patent,
    vehiclePatent: route?.vehiclePatent,
    transportType: route?.transportType
  });

const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

export const HistoryDetailModal = ({ show, onClose, route, settings, onOpenRoute }) => {
  const officeLocation = useMemo(
    () => getOfficeLocationFromSettings(settings),
    [settings]
  );

  const { totalCost, legCosts } = useMemo(
    () => calculateRouteCost({ route, settings, officeLocation }),
    [route, settings, officeLocation]
  );


  const reportEntries = useMemo(() => {
    if (!route || !route.destinations) return [];

    const completedOrIncompleteDestinations = route.destinations
      .filter(
        (d) =>
          (d.status === 'completed' || d.status === 'incomplete') &&
          d.completedAt &&
          d.latlng &&
          typeof d.latlng.lat === 'number' &&
          typeof d.latlng.lng === 'number'
      )
      .sort((a, b) => getTimestampMillis(a.completedAt) - getTimestampMillis(b.completedAt));

    const cancelledDestinations = route.destinations.filter((d) => d.status === 'cancelled');

    let lastPointCoords = { lat: officeLocation.lat, lng: officeLocation.lng };
    let lastPointName = officeLocation.name;

    const entries = [];

    completedOrIncompleteDestinations.forEach((dest, index) => {
      const currentDestCoords = dest.latlng;
      const distance = calculateDistance(
        lastPointCoords.lat,
        lastPointCoords.lng,
        currentDestCoords.lat,
        currentDestCoords.lng
      );

      entries.push({
        stopNumber: index + 1,
        destinationName: dest.name,
        status: dest.status === 'completed' ? 'Completado' : 'Incompleto',
        time:
          typeof dest.completedAt?.toDate === 'function'
            ? dest.completedAt.toDate().toLocaleTimeString('es-AR', {
                hour: '2-digit',
                minute: '2-digit'
              })
            : new Date(dest.completedAt).toLocaleTimeString('es-AR', {
                hour: '2-digit',
                minute: '2-digit'
              }),
        leg: `${lastPointName} -> ${dest.name}`,
        distance: distance.toFixed(2),
        cost: legCosts[dest.id] || 0
      });

      lastPointCoords = currentDestCoords;
      lastPointName = dest.name;
    });

    cancelledDestinations.forEach((dest) => {
      entries.push({
        stopNumber: entries.length + 1,
        destinationName: dest.name,
        status: 'Cancelado',
        time:
          dest.completedAt?.toDate?.().toLocaleTimeString('es-AR', {
            hour: '2-digit',
            minute: '2-digit'
          }) || 'N/A',
        leg: 'N/A',
        distance: '0.00',
        cost: 0
      });
    });

    return entries;
  }, [route, legCosts, officeLocation]);

  const totalTime = useMemo(() => {
    const completedDests = route?.destinations
      ?.filter((d) => (d.status === 'completed' || d.status === 'incomplete') && d.completedAt)
      .sort((a, b) => getTimestampMillis(a.completedAt) - getTimestampMillis(b.completedAt));

    if (!completedDests || completedDests.length < 1) return '0 min';

    const startTime =
      typeof completedDests[0].completedAt?.toDate === 'function'
        ? completedDests[0].completedAt.toDate()
        : new Date(completedDests[0].completedAt);

    const endTime =
      typeof completedDests[completedDests.length - 1].completedAt?.toDate === 'function'
        ? completedDests[completedDests.length - 1].completedAt.toDate()
        : new Date(completedDests[completedDests.length - 1].completedAt);

    let diffMs = endTime - startTime;
    if (completedDests.length === 1) diffMs = 0;

    const diffHrs = Math.floor(diffMs / 3600000);
    const diffMins = Math.round((diffMs % 3600000) / 60000);

    if (diffHrs > 0) return `${diffHrs} hs ${diffMins} min`;
    return `${diffMins} min`;
  }, [route]);

  const totalDistance = useMemo(() => {
    return reportEntries.reduce((sum, entry) => sum + parseFloat(entry.distance || 0), 0).toFixed(2);
  }, [reportEntries]);

  if (!show || !route) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const reportElement = document.getElementById(`printable-report-modal-${route.id}`);
    if (reportElement) {
      html2canvas(reportElement).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`informe-ruta-${route.date}.pdf`);
      });
    }
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden;
      }
      #printable-report-modal-${route.id}, #printable-report-modal-${route.id} * {
        visibility: visible;
      }
      #printable-report-modal-${route.id} {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        padding: 20px;
        background: white;
      }
    }
  `;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
      <style>{printStyles}</style>

      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-slate-800">Detalle de Ruta Histórica</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="bg-blue-100 text-blue-700 py-2 px-3 rounded-md hover:bg-blue-200 flex items-center gap-2 font-semibold"
            >
              <Icon
                path="M6.72 13.829c-.34.052-.668.087-.99.111v-2.42a.75.75 0 01.75-.75h13.5a.75.75 0 01.75.75v2.42a49.048 49.048 0 01-.99-.111c-.34-.052-.668-.087-.99-.111m-11.454 0c1.297-.138 2.65-.25 4.005-.327m-4.005.327c1.297-.138 2.65-.25 4.005.327m7.449 0c1.355.077 2.708.189 4.005.327M4.5 9.75a.75.75 0 01.75-.75h13.5a.75.75 0 01.75.75v10.5a.75.75 0 01-.75-.75H5.25a.75.75 0 01-.75-.75V9.75z"
                className="w-5 h-5"
              />
              Imprimir
            </button>

            <button
              type="button"
              onClick={handleDownload}
              className="bg-green-100 text-green-700 py-2 px-3 rounded-md hover:bg-green-200 flex items-center gap-2 font-semibold"
            >
              <Icon
                path="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                className="w-5 h-5"
              />
              Descargar
            </button>

            {typeof onOpenRoute === 'function' ? (
              <button
                type="button"
                onClick={() => onOpenRoute(route)}
                className="bg-slate-100 text-slate-700 py-2 px-3 rounded-md hover:bg-slate-200 flex items-center gap-2 font-semibold"
              >
                <Icon
                  path="M3.75 5.25h16.5M3.75 12h16.5m-16.5 6.75h16.5"
                  className="w-5 h-5"
                />
                Ir a la ruta
              </button>
            ) : null}

            <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-200">
              <Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div id={`printable-report-modal-${route.id}`} className="overflow-y-auto">
          <h3 className="text-xl font-bold text-slate-700 mb-1">{route.name}</h3>
          <div className="mb-4 grid gap-3 rounded-2xl bg-slate-50 p-4 md:grid-cols-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Chofer</p>
              <p className="mt-1 text-sm font-bold text-slate-900">{getRouteDriverLabel(route)}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Unidad</p>
              <p className="mt-1 text-sm font-bold text-slate-900">{getRouteTransportLabel(route)}</p>
              <p className="mt-1 text-xs text-slate-500">{getRouteTransportMeta(route) || 'Sin detalle adicional'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Fecha</p>
              <p className="mt-1 text-sm font-bold text-slate-900">{route?.date ? new Date(`${route.date}T00:00:00`).toLocaleDateString('es-AR') : 'Sin fecha'}</p>
            </div>
          </div>

          <table className="min-w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Parada</th>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Destino</th>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Estado</th>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Hora</th>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Distancia (km)</th>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Costo ($)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {reportEntries.map((entry) => (
                <tr key={entry.stopNumber}>
                  <td className="py-3 px-3 text-sm font-medium text-slate-700">{entry.stopNumber}°</td>
                  <td className="py-3 px-3 text-sm text-slate-600">{entry.destinationName}</td>
                  <td className="py-3 px-3 text-sm">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        entry.status === 'Completado'
                          ? 'bg-green-100 text-green-700'
                          : entry.status === 'Incompleto'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {entry.status}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-sm text-slate-600">{entry.time}</td>
                  <td className="py-3 px-3 text-sm text-slate-500">{entry.distance}</td>
                  <td className="py-3 px-3 text-sm font-semibold text-slate-800">
                    {formatCurrency(entry.cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 pt-4 border-t-2 border-dashed">
            <h4 className="text-lg font-bold text-slate-700 mb-2">Resumen del Recorrido</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-slate-100 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Km Totales</p>
                <p className="text-2xl font-bold text-slate-800">{totalDistance}</p>
              </div>
              <div className="bg-slate-100 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Tiempo Total</p>
                <p className="text-2xl font-bold text-slate-800">{totalTime}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <p className="text-sm text-green-700">Costo Total</p>
                <p className="text-2xl font-bold text-green-800">$ {formatCurrency(totalCost)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AnnualExpenseDetailView = ({ transporteData, flotaData, totalTransporte, totalFlota }) => {
  return (
    <div className="p-4 rounded-lg bg-red-50 border border-red-200 animate-fade-in mt-6">
      <h4 className="text-xl font-bold text-red-800 mb-4">Desglose de Egresos Anuales</h4>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="max-h-[300px] overflow-y-auto">
          <h5 className="font-bold text-slate-700 mb-2 sticky top-0 bg-red-50 pb-2 border-b border-red-200">
            Pago Transportistas: $ {formatCurrency(totalTransporte)}
          </h5>

          <table className="min-w-full text-sm">
            <thead className="bg-red-100">
              <tr>
                <th className="p-2 text-left">Fecha</th>
                <th className="p-2 text-left">Ruta</th>
                <th className="p-2 text-right">Costo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-red-200">
              {transporteData.map((item) => (
                <tr key={item.id}>
                  <td className="p-2">
                    {new Date(item.date + 'T00:00:00').toLocaleDateString('es-AR')}
                  </td>
                  <td className="p-2">{item.name}</td>
                  <td className="p-2 text-right font-semibold">$ {formatCurrency(item.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="max-h-[300px] overflow-y-auto">
          <h5 className="font-bold text-slate-700 mb-2 sticky top-0 bg-red-50 pb-2 border-b border-red-200">
            Gastos de Flota: $ {formatCurrency(totalFlota)}
          </h5>

          <table className="min-w-full text-sm">
            <thead className="bg-red-100">
              <tr>
                <th className="p-2 text-left">Fecha</th>
                <th className="p-2 text-left">Categoría</th>
                <th className="p-2 text-right">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-red-200">
              {flotaData.map((item) => (
                <tr key={item.id}>
                  <td className="p-2">
                    {new Date(item.date + 'T00:00:00').toLocaleDateString('es-AR')}
                  </td>
                  <td className="p-2">{item.name}</td>
                  <td className="p-2 text-right font-semibold">$ {formatCurrency(item.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Balance General ---

// --- Configuración ---

// --- Modal historial anual ---
export const YearlyHistoryModal = ({ show, onClose, allRoutes, allFleetExpenses, settings }) => {
const yearlyData = useMemo(() => {
  if (!show) return [];

  const officeLocation = getOfficeLocationFromSettings(settings);
  const dataByYear = {};

  [...allRoutes, ...allFleetExpenses].forEach((item) => {
    const year = new Date(item.date + 'T00:00:00').getFullYear();
    if (!dataByYear[year]) {
      dataByYear[year] = { year, costoRutas: 0, gastosFlota: 0 };
    }
  });

  allRoutes.forEach((route) => {
    const year = new Date(route.date + 'T00:00:00').getFullYear();
    if (dataByYear[year]) {
      dataByYear[year].costoRutas += calculateRouteCost({
        route,
        settings,
        officeLocation
      }).totalCost;
    }
  });

  allFleetExpenses.forEach((expense) => {
    const year = new Date(expense.date + 'T00:00:00').getFullYear();
    if (dataByYear[year]) {
      dataByYear[year].gastosFlota += expense.amount;
    }
  });

  return Object.values(dataByYear)
    .map((yearData) => ({
      ...yearData,
      egresoTotal: yearData.costoRutas + yearData.gastosFlota
    }))
    .sort((a, b) => b.year - a.year);
}, [show, allRoutes, allFleetExpenses, settings]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[80]">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col animate-fade-in-down">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-800">Historial de Egresos Anuales</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-slate-200">
            <Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto">
          <table className="min-w-full">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="py-2 px-3 text-left text-sm font-semibold text-slate-600">Año</th>
                <th className="py-2 px-3 text-right text-sm font-semibold text-slate-600">Costo Rutas</th>
                <th className="py-2 px-3 text-right text-sm font-semibold text-slate-600">Gastos Flota</th>
                <th className="py-2 px-3 text-right text-sm font-semibold text-slate-600">Egreso Total</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {yearlyData.map(({ year, costoRutas, gastosFlota, egresoTotal }) => (
                <tr key={year}>
                  <td className="py-3 px-3 font-bold">{year}</td>
                  <td className="py-3 px-3 text-right">{formatCurrency(costoRutas)}</td>
                  <td className="py-3 px-3 text-right">{formatCurrency(gastosFlota)}</td>
                  <td className="py-3 px-3 text-right font-bold text-red-600">
                    {formatCurrency(egresoTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Componente principal ---
